import React from 'react';
import { spacingClasses, cn, sectionBackgroundColors } from '@/lib/utils';
import { ButtonStyleType, SpacingType, sectionVariants } from "@/types";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import Link from 'next/link';
import Image from '@/components/ui/Image';
import { ValidationRules } from '@/components/Form/components/FormValidation';

interface Cta1Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  ctaTitle: string;
  ctaSubtitle: string;
  ctaButtonText: string;
  ctaButtonLink: string;
  ctaButtonStyle: ButtonStyleType;
  ctaImage: string;
  ctaImageAltText: string;
  subscribeFormTitle: string; 
  subscribeFormSubtitle: string;
  subscribeFormButtonText: string;
  attributes?: React.HTMLProps<HTMLElement>;
}

// Form data interface
interface SubscribeFormData {
  email: string;
}

export const Cta1 = ({ ctaTitle, ctaSubtitle, ctaButtonText, ctaButtonLink, ctaButtonStyle, ctaImage, ctaImageAltText, subscribeFormTitle, subscribeFormSubtitle, subscribeFormButtonText, darkMode = false, spacing = 'xlarge', variant = 'A', attributes }: Cta1Props) => {
  const { key, ...rest } = attributes ?? {};
  const spacingClass = spacingClasses[spacing || 'none'];
  const sectionClass = cn(
    spacingClass,
    sectionBackgroundColors(darkMode, variant),
    "content-cta1 text-foreground"
  );

  const gridContainerClass = cn(
    "grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8"
  );
  const promoBlockClass = cn(
    darkMode ? "dark" : "light",  
    "relative overflow-hidden rounded-xl bg-muted p-6 xl:p-8 2xl:p-12 lg:col-span-2"
  );

  const subscribeBlockClass = cn(
    darkMode ? "light" : "dark",  
    "w-full rounded-xl bg-muted p-6 xl:p-8 2xl:p-12 lg:col-span-1"
  );

  const form = useForm<SubscribeFormData>({
    defaultValues: {
      email: "",
    },
    mode: 'onChange', // Enable real-time validation
  });

  function onSubmit(values: SubscribeFormData) {
    console.info(values);
  }
  
  return (
    <section
      key={key}
      {...rest}
      className={sectionClass}
      id={`cta-${key}`} 
      data-trackable-section={`cta-${key}`}
    >
      <div className="container">
        <div className={gridContainerClass}>
          <div className={promoBlockClass}>
            <div className="flex h-full flex-row gap-6">
              <div className="flex flex-col justify-center gap-6 z-10 flex-3/5 text-center md:text-left">
                {ctaTitle && (
                  <h4 className="text-primary">
                    {ctaTitle}
                  </h4>
                )}
                {ctaSubtitle && (
                  <p className="paragraph-base text-muted-foreground">
                    {ctaSubtitle}
                  </p>
                )}
                {ctaButtonText && ctaButtonLink && (
                  <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4 md:justify-start">
                    <Button variant={ctaButtonStyle} className="w-full" size="lg" asChild>
                      <Link href={ctaButtonLink}>
                        {ctaButtonText}
                      </Link>
                    </Button>
                  </div>
                )}
              </div>
              {/* Image */}
              <div className="relative hidden md:block flex-2/5 h-full">
                {ctaImage && (
                  <div className='absolute -bottom-6 xl:-bottom-8 2xl:-bottom-12 inset-0 flex items-end'>
                    <Image
                      src={ctaImage}
                      alt={ctaImageAltText ?? ''}
                      title={ctaImageAltText ?? ''}
                      className="h-full w-auto object-cover"
                      width={350}
                      height={350}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={subscribeBlockClass}>
            <div className="w-full h-full flex flex-col items-center justify-center gap-4 xl:gap-8">
              <div className='w-full'>
                {subscribeFormTitle && (
                  <h4 className="mb-4 text-gradient-primary">
                    {subscribeFormTitle}
                  </h4>
                )}
                {subscribeFormSubtitle && (
                  <p className="paragraph-base text-foreground">
                    {subscribeFormSubtitle}
                  </p>
                )}
              </div>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
                  <div className="space-y-4 xl:space-y-8">
                    <FormField
                      control={form.control}
                      name="email"
                      rules={ValidationRules.email()}
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <Input
                              {...field}
                              type="email"
                              id="emailInput"
                              placeholder="Enter your email"
                              className="w-full focus-visible:ring-0 focus-visible:ring-offset-0"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button type="submit" className="w-full" size="lg">
                      {subscribeFormButtonText}
                    </Button>
                  </div>
                </form>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}; 